import { Box, Collapse, Flex, FlexProps, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { useActiveAccount, useObservableState } from "applesauce-react/hooks";

import { connections$ } from "../../../services/pool";
import useNwcWallet from "../../../hooks/use-nwc-wallet";
import useUserWallet from "../../../hooks/use-user-wallet";
import { isWalletUnlocked } from "applesauce-wallet/helpers";
import { getAverageMs, getTimingCount } from "../../../services/telemetry";

function Indicator({ color, letter, tooltip }: { color: string; letter: string; tooltip: string }) {
  return (
    <Tooltip label={tooltip} hasArrow placement="top">
      <Flex alignItems="center" gap="1" cursor="default">
        <Box w="1.5" h="1.5" borderRadius="full" bg={color} flexShrink={0} />
        <Text fontSize="2xs" fontWeight="bold" color={color} lineHeight="1">
          {letter}
        </Text>
      </Flex>
    </Tooltip>
  );
}

export default function ConnectionStatus({ compact: _compact, ...props }: { compact?: boolean } & FlexProps) {
  const account = useActiveAccount();
  const { isOpen, onToggle } = useDisclosure();

  // Nostr relay
  const connections = useObservableState(connections$) ?? {};
  const connected = Object.values(connections).reduce((t, s) => (s === "connected" ? t + 1 : t), 0);
  const total = Object.keys(connections).length;
  const relayColor = connected > 0 ? "green.400" : "red.400";
  const relayTooltip = `Nostr: ${connected}/${total} relays`;

  // Lightning (NWC)
  const { connection: nwcConn, error: nwcError, isLoading: nwcLoading } = useNwcWallet();
  const lnColor = nwcConn ? "green.400" : nwcLoading ? "orange.400" : "red.400";
  const lnTooltip = nwcConn
    ? "Lightning: connected"
    : nwcLoading
      ? "Lightning: connecting..."
      : nwcError
        ? `Lightning: ${nwcError}`
        : "Lightning: offline";

  // eCash (Cashu)
  const wallet = useUserWallet(account?.pubkey);
  const cashuUnlocked = wallet ? isWalletUnlocked(wallet) : false;
  const eCashColor = !wallet ? "red.400" : cashuUnlocked ? "green.400" : "orange.400";
  const eCashTooltip = !wallet ? "eCash: no wallet" : cashuUnlocked ? "eCash: unlocked" : "eCash: locked";

  // Telemetry stats
  const avgTipMs = getAverageMs("tip");
  const avgPayMs = getAverageMs("lightning_pay");
  const timingCount = getTimingCount();

  return (
    <Box>
      <Flex
        gap="2"
        alignItems="center"
        cursor="pointer"
        onClick={onToggle}
        borderRadius="md"
        px="1"
        _hover={{ bg: "whiteAlpha.100" }}
        {...props}
      >
        <Indicator color={relayColor} letter="N" tooltip={relayTooltip} />
        {account && <Indicator color={lnColor} letter="L" tooltip={lnTooltip} />}
        {account && <Indicator color={eCashColor} letter="E" tooltip={eCashTooltip} />}
      </Flex>
      <Collapse in={isOpen}>
        <Box p="2" mt="1" bg="gray.800" borderRadius="md" fontSize="xs" lineHeight="tall">
          <Text>
            Relay: nostr.litstrudel.xyz — {connected > 0 ? "Connected" : "Disconnected"} ({connected}/{total})
          </Text>
          {account && (
            <Text>
              Lightning: {nwcConn ? "Connected" : "Offline"}
            </Text>
          )}
          {account && (
            <Text>
              ECash: {!wallet ? "No wallet" : cashuUnlocked ? "Unlocked" : "Locked"}
            </Text>
          )}
          {timingCount > 0 && (
            <>
              {avgTipMs !== null && <Text>Avg tip: {(avgTipMs / 1000).toFixed(1)}s</Text>}
              {avgPayMs !== null && <Text>Avg payment: {(avgPayMs / 1000).toFixed(1)}s</Text>}
              <Text color="gray.500">{timingCount} operations recorded</Text>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
